import type { Question } from '@examflow/core';
import { looksLikeOpenEndedClozeStem, QuestionType } from '@examflow/core';
import {
  getContentTopicsForScope,
  getWritingSuggestedMinutes,
  getWritingTaskOptionsForExam,
  normalizeWritingPerformancePercent,
} from '@examflow/exams';

export type OpenEndedQuestionMeta = {
  taskNumber: number | null;
  minWordCount: number;
  maxWordCount: number | null;
};

/** Learner-facing chips / chrome for writing prompt / history rows. */
export type WritingDisplayLabels = {
  taskLabel: string;
  /** True when inventory omitted taskNumber — word window may use Task 1 defaults. */
  taskUnresolved: boolean;
  taskHint: string | null;
  wordWindowLabel: string;
  /** Soft prep time, e.g. `建议 20 分钟`. */
  suggestedTimeLabel: string | null;
  /** Soft suggested minutes (never a hard gate); null when unknown. */
  suggestedMinutes: number | null;
  /** Chrome subtitle: Task · word window · suggested time · stop-anytime. */
  chromeSummaryLine: string;
  topicLabels: string[];
  /** Prefixed difficulty chip (e.g. `难度 B1`) — never a bare digit that looks like a task. */
  levelLabel: string | null;
};

type LooseQuestion = Question & {
  responseMode?: string;
  taskNumber?: number;
  task?: number;
  minWordCount?: number;
  maxWordCount?: number;
  topics?: string[];
};

/** Whitespace-delimited word count (French / English writing tasks). */
export function countEssayWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).filter(Boolean).length;
}

/** Closed fill-blank answer key — writing open-ended should omit / empty this. */
function hasClosedFillBlankAnswer(answer: unknown): boolean {
  if (answer == null) return false;
  if (typeof answer === 'string') return answer.trim().length > 0;
  if (Array.isArray(answer)) return answer.length > 0;
  return true;
}

/**
 * Usable open-ended writing Question for Training Session assembly.
 * Rejects cloze stems (______) even when mistagged responseMode=open_ended —
 * those belong to grammar inventories, not writing Runtime.
 */
export function isUsableOpenEndedQuestion(question: Question): boolean {
  if (
    Array.isArray(question.options) &&
    question.options.length > 0 &&
    question.options.every((option) => option.id && option.content) &&
    !!question.correctAnswer
  ) {
    return false;
  }
  const content = typeof question.content === 'string' ? question.content.trim() : '';
  if (!content) return false;
  // Production pollution: fill_blank cloze stems land in writing inventory.
  if (looksLikeOpenEndedClozeStem(content)) return false;

  const loose = question as LooseQuestion;
  if (loose.responseMode === 'open_ended') return true;

  // Legacy carrier without explicit responseMode: fill_blank, no options, no answer key.
  if (question.type !== QuestionType.FILL_BLANK) return false;
  if (question.options && question.options.length > 0) return false;
  if (hasClosedFillBlankAnswer(question.correctAnswer)) return false;
  return true;
}

type WordWindow = { minWordCount: number; maxWordCount: number | null };

/** Exam-aware defaults when inventory omitted min/maxWordCount. */
function defaultWordWindow(
  examId: string | undefined,
  taskNumber: number | null
): WordWindow {
  const exam = String(examId ?? '')
    .trim()
    .toLowerCase();

  if (exam === 'ielts') {
    if (taskNumber === 2) return { minWordCount: 250, maxWordCount: null };
    // Task 1 or unknown → Academic/GT Task 1 floor
    return { minWordCount: 150, maxWordCount: null };
  }

  // TCF Expression écrite (and other exams without explicit windows): tâche defaults
  if (taskNumber === 1) return { minWordCount: 60, maxWordCount: 120 };
  if (taskNumber === 2) return { minWordCount: 120, maxWordCount: 150 };
  if (taskNumber === 3) return { minWordCount: 120, maxWordCount: 180 };
  return { minWordCount: 60, maxWordCount: null };
}

export function resolveOpenEndedMeta(
  question: Question,
  examId?: string
): OpenEndedQuestionMeta {
  const loose = question as LooseQuestion;
  const taskRaw = Number(loose.taskNumber ?? loose.task);
  const taskNumber = Number.isFinite(taskRaw) && taskRaw > 0 ? Math.trunc(taskRaw) : null;
  const defaults = defaultWordWindow(examId, taskNumber);
  const minWordCount =
    typeof loose.minWordCount === 'number' && loose.minWordCount > 0
      ? loose.minWordCount
      : defaults.minWordCount;
  const maxWordCount =
    typeof loose.maxWordCount === 'number' && loose.maxWordCount > 0
      ? loose.maxWordCount
      : defaults.maxWordCount;
  return { taskNumber, minWordCount, maxWordCount };
}

export function formatWritingWordWindowLabel(meta: OpenEndedQuestionMeta): string {
  if (meta.maxWordCount != null) {
    return `${meta.minWordCount}–${meta.maxWordCount} 词`;
  }
  return `至少 ${meta.minWordCount} 词`;
}

export function resolveQuestionTopicIds(question: Question): string[] {
  const topics = (question as LooseQuestion).topics;
  if (!Array.isArray(topics)) return [];
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const raw of topics) {
    const id = String(raw ?? '')
      .trim()
      .toLowerCase();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

export function resolveWritingTopicLabels(
  examId: string | null | undefined,
  topicIds: string[] | null | undefined,
  skillId = 'writing'
): string[] {
  if (!topicIds?.length) return [];
  const catalog = getContentTopicsForScope(examId ?? '', skillId);
  const byId = new Map(catalog.map((topic) => [topic.id, topic.label]));
  const labels: string[] = [];
  for (const id of topicIds) {
    const normalized = id.trim().toLowerCase();
    if (!normalized) continue;
    labels.push(byId.get(normalized) ?? normalized);
  }
  return labels;
}

function formatWritingLevelChip(level: string | null | undefined): string | null {
  const trimmed = typeof level === 'string' ? level.trim() : '';
  if (!trimmed) return null;
  return `难度 ${trimmed}`;
}

/**
 * Resolve learner-facing task / word-window / topic chips for a writing Question.
 */
export function resolveWritingDisplayLabels(
  question: Question,
  examId?: string | null
): WritingDisplayLabels {
  const meta = resolveOpenEndedMeta(question, examId ?? undefined);
  const taskOptions = getWritingTaskOptionsForExam(examId);
  const taskUnresolved = meta.taskNumber == null;
  const taskOption =
    meta.taskNumber != null
      ? taskOptions.find((option) => option.value === meta.taskNumber)
      : undefined;
  const taskLabel =
    taskOption?.label ??
    (meta.taskNumber != null ? `任务 ${meta.taskNumber}` : '未标注任务');
  const wordWindowLabel = formatWritingWordWindowLabel(meta);
  const suggestedMinutes = getWritingSuggestedMinutes(examId, meta.taskNumber);
  const suggestedTimeLabel =
    suggestedMinutes != null ? `建议 ${suggestedMinutes} 分钟` : null;
  const defaultTaskHint =
    taskUnresolved && taskOptions[0]
      ? `未标注任务 · 词数按 ${taskOptions[0].label} 计`
      : null;
  const topicIds = resolveQuestionTopicIds(question);

  const chromeParts = [taskLabel, wordWindowLabel, suggestedTimeLabel, '随时可停'].filter(
    Boolean
  );

  return {
    taskLabel,
    taskUnresolved,
    taskHint: taskOption?.hint ?? defaultTaskHint,
    wordWindowLabel,
    suggestedTimeLabel,
    suggestedMinutes: suggestedMinutes ?? null,
    chromeSummaryLine: chromeParts.join(' · '),
    topicLabels: resolveWritingTopicLabels(examId, topicIds),
    levelLabel: formatWritingLevelChip(
      typeof question.level === 'string' ? question.level : null
    ),
  };
}

/** Pick the lowest-scoring rubric dimension for revision focus. */
export function pickWeakestWritingDimension<
  T extends { id: string; label: string; score: number; action: string },
>(dimensions: T[]): T | null {
  if (!dimensions.length) return null;
  const ranked = [...dimensions].sort(
    (a, b) => a.score - b.score || a.id.localeCompare(b.id)
  );
  return ranked[0] ?? null;
}

/**
 * Map AI writing score onto Training Attempt `accuracy` (0–100 performance).
 * Pass rubric scoreMax from grade response; omit only for legacy rows (defaults to 20).
 */
export function normalizeWritingAccuracyFromScore(
  aiScore: number | undefined | null,
  scoreMax?: number | null
): number {
  return normalizeWritingPerformancePercent(aiScore, scoreMax);
}

export const FRENCH_SPECIAL_CHARS_LOWER = [
  'à',
  'â',
  'ä',
  'æ',
  'ç',
  'é',
  'è',
  'ê',
  'ë',
  'î',
  'ï',
  'ô',
  'œ',
  'ù',
  'û',
  'ü',
] as const;

export const FRENCH_SPECIAL_CHARS_UPPER = [
  'À',
  'Â',
  'Ä',
  'Æ',
  'Ç',
  'É',
  'È',
  'Ê',
  'Ë',
  'Î',
  'Ï',
  'Ô',
  'Œ',
  'Ù',
  'Û',
  'Ü',
] as const;
